import React, { useEffect, useMemo, useState } from 'react'
import {
  FaAward,
  FaCalendarAlt,
  FaChartLine,
  FaGraduationCap,
  FaMedal,
  FaStar,
  FaTrophy,
} from 'react-icons/fa'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Achievement/Merit Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const parsePercentage = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(String(v).replace(/%/g, '').trim())
  return Number.isFinite(n) ? n : null
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'Certificate of Achievement'
  return t
}

const CertificateTemplate1 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  date = '',
  instituteName = 'Institute Name',
  certificateTitle = 'Certificate of Achievement',
  authorityName = 'Authorized Signatory',
  logoUrl = '',
  certificateId = '',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  /** When true, parent already applies sidebar offset (e.g. TemplateBasedCertificate, modals). */
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined

    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const pct = parsePercentage(certificate.percentage ?? certificate.percent)
    const grade = safeStr(certificate.grade)
    const rank =
      certificate.rank != null && String(certificate.rank).trim() !== ''
        ? String(certificate.rank).trim()
        : student.rank != null && String(student.rank).trim() !== ''
          ? String(student.rank).trim()
          : ''

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const titlePrinted = displayTitleFromProps(certificateTitle)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      pct,
      grade,
      rank,
      photoSrc,
      titlePrinted,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    pct,
    grade,
    rank,
    photoSrc,
    titlePrinted,
  } = data

  const withDistinction = pct !== null && pct > 90
  const coursePeriod =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=80x80&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl

  const handlePrint = () => {
    window.print()
  }

  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  return (
    <div
      className="certificate-template-1-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,500&family=Source+Sans+3:wght@400;500;600;700&display=swap');

          .certificate-template-1-page {
            min-height: 100vh;
            background: linear-gradient(165deg, #e8edf5 0%, #f4f6fb 40%, #dce4f2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px 16px 32px;
            box-sizing: border-box;
          }

          .certificate-template-1-wrap {
            width: min(297mm, 100%);
          }

          .certificate-template-1-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 14px;
          }

          .certificate-template-1-print-btn {
            border: none;
            background: linear-gradient(135deg, #1e3a8a, #0f766e);
            color: #fff;
            padding: 10px 20px;
            border-radius: 10px;
            font-weight: 700;
            font-family: 'Source Sans 3', system-ui, sans-serif;
            letter-spacing: 0.4px;
            cursor: pointer;
            box-shadow: 0 8px 20px rgba(30, 58, 138, 0.25);
          }

          .certificate-template-1-sheet {
            --gold: #c5a028;
            --gold-deep: #8b6914;
            --navy: #1e3a8a;
            --navy-dark: #0f172a;
            --paper: #fefdfb;
            aspect-ratio: 297 / 210;
            width: 100%;
            max-height: min(90vh, 210mm);
            background: var(--paper);
            position: relative;
            overflow: hidden;
            box-shadow: 0 24px 56px rgba(15, 23, 42, 0.14);
            border-radius: 4px;
          }

          .certificate-template-1-frame-outer {
            position: absolute;
            inset: 10px;
            border: 3px double var(--gold);
            border-radius: 2px;
            pointer-events: none;
          }

          .certificate-template-1-frame-inner {
            position: absolute;
            inset: 18px;
            border: 1px solid rgba(30, 58, 138, 0.35);
            border-radius: 1px;
            pointer-events: none;
          }

          .certificate-template-1-pattern {
            position: absolute;
            inset: 0;
            opacity: 0.045;
            background-image:
              radial-gradient(circle at 20% 30%, var(--navy) 0, transparent 45%),
              radial-gradient(circle at 80% 70%, var(--gold-deep) 0, transparent 40%),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 12px,
                rgba(30, 58, 138, 0.08) 12px,
                rgba(30, 58, 138, 0.08) 13px
              );
            pointer-events: none;
          }

          .certificate-template-1-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(48px, 14vw, 120px);
            font-weight: 700;
            color: rgba(30, 58, 138, 0.06);
            letter-spacing: 0.12em;
            text-transform: uppercase;
            pointer-events: none;
            user-select: none;
          }

          .certificate-template-1-header-strip {
            height: 12px;
            background: linear-gradient(90deg, #1e3a8a 0%, #1d4ed8 35%, #b8860b 100%);
            position: relative;
            z-index: 2;
          }

          .certificate-template-1-body {
            position: relative;
            z-index: 2;
            padding: clamp(14px, 2.2vw, 28px) clamp(18px, 3vw, 36px) clamp(16px, 2vw, 24px);
            height: calc(100% - 12px);
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }

          .certificate-template-1-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 10px;
          }

          .certificate-template-1-logo-wrap {
            flex-shrink: 0;
            width: 72px;
            height: 72px;
            border-radius: 50%;
            border: 3px double var(--gold);
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .certificate-template-1-logo-wrap img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-1-logo-ph {
            font-family: 'Cormorant Garamond', serif;
            font-size: 11px;
            font-weight: 700;
            color: #94a3b8;
            text-align: center;
            line-height: 1.2;
            padding: 6px;
          }

          .certificate-template-1-heading-block {
            flex: 1;
            text-align: center;
            min-width: 0;
          }

          .certificate-template-1-institute {
            margin: 0;
            font-family: 'Cormorant Garamond', serif;
            font-weight: 700;
            font-size: clamp(22px, 3.2vw, 34px);
            color: var(--navy);
            letter-spacing: 0.02em;
            line-height: 1.15;
          }

          .certificate-template-1-title {
            margin: 6px 0 0;
            font-family: 'Cormorant Garamond', serif;
            font-weight: 700;
            font-size: clamp(26px, 3.8vw, 38px);
            color: var(--navy-dark);
            letter-spacing: 0.14em;
            text-transform: uppercase;
          }

          .certificate-template-1-title-accent {
            display: block;
            width: min(280px, 70%);
            height: 3px;
            margin: 10px auto 0;
            background: linear-gradient(90deg, transparent, var(--gold), transparent);
            border-radius: 2px;
          }

          .certificate-template-1-badges {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px;
            margin-top: 10px;
          }

          .certificate-template-1-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-family: 'Source Sans 3', sans-serif;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            padding: 4px 12px;
            border-radius: 999px;
            border: 1px solid rgba(197, 160, 40, 0.5);
            color: var(--gold-deep);
            background: rgba(197, 160, 40, 0.12);
          }

          .certificate-template-1-badge--dist {
            border-color: #15803d;
            color: #14532d;
            background: rgba(22, 163, 74, 0.12);
          }

          .certificate-template-1-main {
            display: grid;
            grid-template-columns: 1fr min(140px, 28%);
            gap: clamp(12px, 2vw, 24px);
            align-items: start;
            flex: 1;
            min-height: 0;
          }

          .certificate-template-1-prose {
            font-family: 'Source Sans 3', system-ui, sans-serif;
            font-size: clamp(13px, 1.35vw, 15px);
            line-height: 1.65;
            color: #334155;
            text-align: center;
            margin: 0;
          }

          .certificate-template-1-name {
            display: inline;
            font-family: 'Cormorant Garamond', serif;
            font-weight: 700;
            font-size: clamp(20px, 2.6vw, 28px);
            color: var(--navy);
            border-bottom: 2px solid rgba(30, 58, 138, 0.35);
            padding-bottom: 2px;
          }

          .certificate-template-1-em {
            font-weight: 600;
            color: #1e293b;
          }

          .certificate-template-1-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .certificate-template-1-photo {
            width: 100%;
            max-width: 132px;
            aspect-ratio: 3 / 4;
            border: 4px double var(--gold);
            border-radius: 6px;
            overflow: hidden;
            background: #f1f5f9;
            box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);
          }

          .certificate-template-1-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-1-photo-ph {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            color: #94a3b8;
            text-align: center;
            padding: 8px;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-1-qr {
            text-align: center;
          }

          .certificate-template-1-qr img {
            display: block;
            margin: 0 auto;
            width: 72px;
            height: 72px;
          }

          .certificate-template-1-qr-cap {
            font-size: 9px;
            color: #64748b;
            margin-top: 4px;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-1-stats {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 8px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid rgba(30, 58, 138, 0.15);
          }

          .certificate-template-1-stat {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 8px 10px;
            background: rgba(30, 58, 138, 0.04);
            border: 1px solid rgba(30, 58, 138, 0.12);
            border-radius: 8px;
            min-width: 0;
          }

          .certificate-template-1-stat-icon {
            color: var(--navy);
            flex-shrink: 0;
            margin-top: 2px;
            font-size: 16px;
          }

          .certificate-template-1-stat-label {
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #64748b;
            font-weight: 700;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-1-stat-value {
            font-size: 12px;
            font-weight: 600;
            color: #0f172a;
            font-family: 'Source Sans 3', sans-serif;
            word-break: break-word;
          }

          .certificate-template-1-meta {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 16px 28px;
            margin-top: 10px;
            font-family: 'Source Sans 3', sans-serif;
            font-size: 11px;
            color: #475569;
          }

          .certificate-template-1-meta span strong {
            color: #1e293b;
            font-weight: 700;
          }

          .certificate-template-1-signatures {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            margin-top: auto;
            padding-top: 12px;
          }

          .certificate-template-1-sign {
            text-align: center;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-1-sign-line {
            border-top: 1px solid #1e293b;
            margin-top: 36px;
            padding-top: 6px;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: #334155;
          }

          .certificate-template-1-sign-sub {
            font-size: 9px;
            color: #64748b;
            margin-top: 2px;
            font-weight: 500;
            text-transform: none;
            letter-spacing: 0;
          }

          .certificate-template-1-seal-circle {
            width: 56px;
            height: 56px;
            margin: 0 auto;
            border: 3px double var(--gold-deep);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            font-weight: 800;
            color: var(--gold-deep);
            text-transform: uppercase;
            line-height: 1.15;
            background: linear-gradient(145deg, #fffbeb, #fef3c7);
            margin-bottom: 8px;
          }

          .certificate-template-1-seal-circle svg {
            width: 26px;
            height: 26px;
          }

          @media (max-width: 720px) {
            .certificate-template-1-main {
              grid-template-columns: 1fr;
            }

            .certificate-template-1-side {
              flex-direction: row;
              justify-content: center;
              flex-wrap: wrap;
            }

            .certificate-template-1-stats {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .certificate-template-1-signatures {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 10mm;
            }

            body * {
              visibility: hidden;
            }

            .certificate-template-1-page,
            .certificate-template-1-page * {
              visibility: visible;
            }

            .certificate-template-1-page {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              min-height: auto;
              padding: 0;
              background: #fff;
              margin-left: 0 !important;
            }

            .certificate-template-1-actions,
            .certificate-template-1-actions * {
              display: none !important;
            }

            .certificate-template-1-wrap {
              width: 100%;
            }

            .certificate-template-1-sheet {
              max-height: none;
              box-shadow: none;
              border-radius: 0;
              aspect-ratio: auto;
              min-height: 190mm;
            }
          }
        `}
      </style>

      <div className="certificate-template-1-wrap">
        <div className="certificate-template-1-actions">
          <button type="button" className="certificate-template-1-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <article className="certificate-template-1-sheet">
          <div className="certificate-template-1-pattern" aria-hidden />
          <div className="certificate-template-1-watermark" aria-hidden>
            Excellence
          </div>
          <div className="certificate-template-1-frame-outer" />
          <div className="certificate-template-1-frame-inner" />

          <div className="certificate-template-1-header-strip" />

          <div className="certificate-template-1-body">
            <div className="certificate-template-1-top">
              <div className="certificate-template-1-logo-wrap">
                {logoResolved ? (
                  <img src={logoResolved} alt="" />
                ) : (
                  <span className="certificate-template-1-logo-ph">Institute Logo</span>
                )}
              </div>

              <div className="certificate-template-1-heading-block">
                <h2 className="certificate-template-1-institute">{instName || 'Institute Name'}</h2>
                <h1 className="certificate-template-1-title">{titlePrinted}</h1>
                <span className="certificate-template-1-title-accent" />
                <div className="certificate-template-1-badges">
                  {withDistinction ? (
                    <span className="certificate-template-1-badge certificate-template-1-badge--dist">
                      <FaStar aria-hidden /> With Distinction
                    </span>
                  ) : null}
                  {rank ? (
                    <span className="certificate-template-1-badge">
                      <FaMedal aria-hidden /> Rank {rank}
                    </span>
                  ) : null}
                </div>
              </div>

              <div style={{ width: 72, flexShrink: 0 }} aria-hidden />
            </div>

            <div className="certificate-template-1-main">
              <div>
                <p className="certificate-template-1-prose">
                  This is to certify that{' '}
                  <span className="certificate-template-1-name">{studName || '—'}</span>
                  {father || mother ? (
                    <>
                      ,<br />
                      {father ? (
                        <>
                          S/o <span className="certificate-template-1-em">{father}</span>
                          {mother ? ' and ' : ''}
                        </>
                      ) : null}
                      {mother ? (
                        <>
                          M/o <span className="certificate-template-1-em">{mother}</span>
                        </>
                      ) : null}
                      ,
                    </>
                  ) : admission ? (
                    ','
                  ) : null}
                  {admission ? (
                    <>
                      <br />
                      bearing Admission No. <span className="certificate-template-1-em">{admission}</span>
                    </>
                  ) : null}
                  , has successfully completed the course{' '}
                  <span className="certificate-template-1-em">{crsName || '—'}</span>
                  {' '}
                  with outstanding performance.
                </p>

                <div className="certificate-template-1-stats">
                  <div className="certificate-template-1-stat">
                    <FaChartLine className="certificate-template-1-stat-icon" aria-hidden />
                    <div>
                      <div className="certificate-template-1-stat-label">Percentage</div>
                      <div className="certificate-template-1-stat-value">
                        {pct !== null ? `${pct}%` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="certificate-template-1-stat">
                    <FaTrophy className="certificate-template-1-stat-icon" aria-hidden />
                    <div>
                      <div className="certificate-template-1-stat-label">Grade</div>
                      <div className="certificate-template-1-stat-value">{grade || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-1-stat">
                    <FaGraduationCap className="certificate-template-1-stat-icon" aria-hidden />
                    <div>
                      <div className="certificate-template-1-stat-label">Course duration</div>
                      <div className="certificate-template-1-stat-value">{duration || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-1-stat">
                    <FaCalendarAlt className="certificate-template-1-stat-icon" aria-hidden />
                    <div>
                      <div className="certificate-template-1-stat-label">Course period</div>
                      <div className="certificate-template-1-stat-value">{coursePeriod || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="certificate-template-1-meta">
                  <span>
                    <strong>Certificate No:</strong> {certNum || '—'}
                  </span>
                  <span>
                    <strong>Issue Date:</strong> {issue || '—'}
                  </span>
                </div>
              </div>

              <aside className="certificate-template-1-side">
                <div className="certificate-template-1-photo">
                  {showPhoto ? (
                    <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                  ) : !fallbackPhotoFailed ? (
                    <img
                      src={defaultPhoto}
                      alt=""
                      onError={() => setFallbackPhotoFailed(true)}
                    />
                  ) : (
                    <div className="certificate-template-1-photo-ph">Student photo</div>
                  )}
                </div>
                <div className="certificate-template-1-qr">
                  {/* <img src={qrSrc} alt="" width={72} height={72} /> */}
                  {/* <div className="certificate-template-1-qr-cap">Scan to verify</div> */}
                </div>
              </aside>
            </div>

            <footer className="certificate-template-1-signatures">
              <div className="certificate-template-1-sign">
                <div className="certificate-template-1-sign-line">{authorityName}</div>
                <div className="certificate-template-1-sign-sub">Authorized Signatory</div>
              </div>
              <div className="certificate-template-1-sign">
                <div className="certificate-template-1-seal-circle">
                  <FaAward aria-hidden />
                </div>
                <div className="certificate-template-1-sign-line">Official Seal</div>
                <div className="certificate-template-1-sign-sub">Institute seal (optional)</div>
              </div>
              <div className="certificate-template-1-sign">
                <div className="certificate-template-1-sign-line">Director / Principal</div>
                <div className="certificate-template-1-sign-sub">Academic Head</div>
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  )
}

export default CertificateTemplate1
